import * as THREE from 'three';
import { CadObject } from '../../../models/cad-object.model';
import {
  MouldCandidatePiece,
  MouldSolution,
} from '../../../models/mould-solution.model';

export const MOULD_SOLUTION_OVERLAY = 'mould-solution-overlay';

export const MOULD_PIECE_COLORS = [
  '#e6194b',
  '#3cb44b',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#42d4f4',
  '#f032e6',
  '#bfef45',
];

export function mouldPieceColor(pieceIndex: number): string {
  return MOULD_PIECE_COLORS[pieceIndex % MOULD_PIECE_COLORS.length];
}

/** Refined pieces are the trimmed form of a solution's pieces, so prefer them when present. */
export function displayedMouldPieces(
  solution: MouldSolution,
): MouldCandidatePiece[] {
  return solution.refined_pieces.length > 0
    ? solution.refined_pieces
    : solution.pieces;
}

/**
 * Highlights each piece of the solution on its CAD object's mesh with its own
 * colour, replacing any previously displayed solution.
 */
export function displayMouldSolution(
  cadObjects: CadObject[],
  solution: MouldSolution | null,
): void {
  clearMouldSolution(cadObjects);
  if (!solution) return;

  displayedMouldPieces(solution).forEach((piece, pieceIndex) => {
    const mesh = cadObjects.find(
      (cadObject) => cadObject.id === piece.cad_object_id,
    )?.mesh;
    if (!mesh) return;

    const overlay = buildPieceOverlay(mesh, piece, pieceIndex);
    if (overlay) mesh.add(overlay);
  });
}

export function clearMouldSolution(cadObjects: CadObject[]): void {
  for (const cadObject of cadObjects) {
    const mesh = cadObject.mesh;
    if (!mesh) continue;

    for (const child of [...mesh.children]) {
      if (child.userData['overlayType'] !== MOULD_SOLUTION_OVERLAY) continue;
      mesh.remove(child);
      (child as THREE.Mesh).geometry.dispose();
      ((child as THREE.Mesh).material as THREE.Material).dispose();
    }
  }
}

function buildPieceOverlay(
  mesh: THREE.Mesh,
  piece: MouldCandidatePiece,
  pieceIndex: number,
): THREE.Mesh | null {
  const positionAttribute = (
    mesh.geometry as THREE.BufferGeometry
  ).getAttribute('position') as THREE.BufferAttribute;
  const positions = positionAttribute.array as Float32Array;
  const triangleCount = positionAttribute.count / 3;

  const overlayPositions: number[] = [];
  for (const triangle of piece.object_face_indices) {
    if (triangle < 0 || triangle >= triangleCount) continue;
    const base = triangle * 9;
    for (let i = 0; i < 9; i++) overlayPositions.push(positions[base + i]);
  }
  if (overlayPositions.length === 0) return null;

  const overlayGeometry = new THREE.BufferGeometry();
  overlayGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(overlayPositions, 3),
  );
  overlayGeometry.computeVertexNormals();

  // Pieces can share faces; offset each piece so later pieces draw on top instead of z-fighting.
  const overlay = new THREE.Mesh(
    overlayGeometry,
    new THREE.MeshStandardMaterial({
      color: mouldPieceColor(pieceIndex),
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -1 - pieceIndex,
      polygonOffsetUnits: -1 - pieceIndex,
    }),
  );
  overlay.userData['overlayType'] = MOULD_SOLUTION_OVERLAY;
  return overlay;
}
